// @givmo/org-console — Profile → Collateral (Email Campaign plan EMC-WEB11, EMC-BE14; D-EMC-20 (d),
// D-EMC-21 (a); mockup 12c): the organization's library of approved graphics, photos and PDFs, which
// its emails, posts and share kits draw from and the assistant picks from.
//
// ══ WHAT EACH ACTION KEEPS ══════════════════════════════════════════════════════════════════════
//
// * **Replace** uploads a new file to the item's stable address: its title, tags and every use that
//   hasn't gone out follow it (D-EMC-20 (d): "Replace keeps its name, tags and uses"). The tile busts its own
//   cache with the item's `replaced_at`, since the address never changes.
// * **Mark out of date** keeps the item and tells the assistant to skip it; **Back in use** undoes it
//   (the menu, and "Put back in use" on the greyed card itself). Only a person marks it: the server
//   never does, and "Date it's for" only labels the card.
// * The ⋯ menu shows on every card, never only on hover: a first-timer sees the actions exist.
// * **Delete** removes it for good (D-EMC-21 (a)). The server refuses it while an unsent email shows
//   the item (409 `COLLATERAL_IN_USE`) and its sentence names those emails; an email that has gone out
//   keeps its own copy either way.
//
// Search and the tag filter are the server's (`q`, `tag`), so a large library filters whole, not one
// page of it. Availability is the read's own answer: a 404 is the calm not-available state (§4).

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  ErrorMessage,
  Field,
  Icon,
  IconButton,
  Menu,
  SearchInput,
  Select,
  Skeleton,
  Textarea,
  cn,
  testIdProp,
} from '@givmo/design-system';
import {
  Archive,
  ArchiveRestore,
  FileText,
  Images,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  COLLATERAL_PAGE_LIMIT,
  addOrgCollateralItem,
  brandRefusalMessage,
  brandUploadRefusal,
  deleteOrgCollateralItem,
  fetchOrgCollateral,
  markOrgCollateralItem,
  orgCollateralKey,
  replaceOrgCollateralFile,
  updateOrgCollateralItem,
  versionedUrl,
  type CollateralItem,
  type CollateralItemPatch,
} from './org-brand-wire';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { PROFILE_COPY } from './profile-copy';

const COPY = PROFILE_COPY.collateral;
const ALL_TAGS = '__all';
const IMAGE_ACCEPT = 'image/png,image/jpeg';
const ANY_ACCEPT = 'image/png,image/jpeg,application/pdf';

export interface OrgCollateralPanelProps {
  orgRef: string;
  /** Whether this person may change the library (the Profile page's own write). */
  canEdit: boolean;
  testIdPrefix: string;
}

/** A search typed into the box reaches the server once the person pauses. */
function useSettled<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** `2025-12-02` → `Dec 2, 2025`, read as a calendar date (never shifted by a time zone). */
export function formatDatedOn(datedOn: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datedOn);
  if (match === null) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

/** The line under an item's title: its description, else the date it is for. */
export function collateralSubline(item: CollateralItem): string | null {
  if (item.description !== null) return item.description;
  const dated = item.datedOn !== null ? formatDatedOn(item.datedOn) : null;
  return dated !== null ? COPY.dated(dated) : null;
}

/** Tags as typed into the details form: comma-separated, trimmed, blanks dropped. */
export function tagsFromText(text: string): string[] {
  return text
    .split(',')
    .map((tag) => tag.trim().replace(/\s+/g, ' '))
    .filter((tag) => tag.length > 0);
}

export function OrgCollateralPanel({ orgRef, canEdit, testIdPrefix }: OrgCollateralPanelProps) {
  const prefix = `${testIdPrefix}-collateral`;
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const q = useSettled(search.trim(), 300);
  const [tag, setTag] = useState<string | null>(null);
  const [uploading, setUploading] = useState<{ id: number; name: string }[]>([]);
  const [failures, setFailures] = useState<string[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);

  const list = useInfiniteQuery({
    queryKey: orgCollateralKey(orgRef, { q, tag }),
    queryFn: ({ pageParam, signal }) =>
      fetchOrgCollateral(orgRef, { q, tag, offset: pageParam, signal }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return last.items.length === COLLATERAL_PAGE_LIMIT && loaded < last.total
        ? loaded
        : undefined;
    },
    retry: false,
    enabled: orgRef.length > 0,
    // A new search keeps the last answer on screen until the next arrives, never a flash of skeletons.
    placeholderData: keepPreviousData,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: orgCollateralKey(orgRef) });

  // Files are added one at a time, in the order chosen; each shows as a tile while it goes up.
  const addFiles = async (chosen: File[]) => {
    let next = Date.now();
    // A file the server would refuse is said at once and never uploaded.
    const refused = chosen.flatMap((file) => {
      const refusal = brandUploadRefusal(file, 'collateral');
      return refusal === null ? [] : [COPY.uploadFailed(file.name, refusal)];
    });
    const files = chosen.filter((file) => brandUploadRefusal(file, 'collateral') === null);
    const pending = files.map((file) => ({ id: next++, name: file.name, file }));
    setFailures(refused);
    setUploading((current) => [...current, ...pending.map(({ id, name }) => ({ id, name }))]);
    for (const { id, name, file } of pending) {
      try {
        await addOrgCollateralItem(orgRef, file);
      } catch (error) {
        setFailures((current) => [
          ...current,
          COPY.uploadFailed(name, brandRefusalMessage(error, COPY.uploadFailedReason)),
        ]);
      }
      setUploading((current) => current.filter((u) => u.id !== id));
      await refresh();
    }
  };

  const first = list.data?.pages[0];
  const items = list.data?.pages.flatMap((page) => page.items) ?? [];
  const tags = first?.tags ?? [];
  const filtering = q.length > 0 || tag !== null;

  if (list.isError && isOrgConsoleUnavailable(list.error)) {
    return (
      <EmptyState
        testId={`${prefix}-unavailable`}
        icon={<Icon icon={Images} size={20} aria-hidden />}
        title={COPY.unavailable.title}
        description={COPY.unavailable.body}
      />
    );
  }

  return (
    <div className="flex flex-col gap-16" {...testIdProp(prefix)}>
      <p className="font-sans text-s14 text-text-secondary">{COPY.lede}</p>
      <div className="flex flex-wrap items-center gap-8">
        <SearchInput
          placeholder={COPY.search}
          aria-label={COPY.search}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch('')}
          containerClassName="w-full sm:w-[280px]"
          testId={`${prefix}-search`}
        />
        <Select
          appearance="saas"
          aria-label={COPY.tagFilter}
          value={tag ?? ALL_TAGS}
          options={[
            { value: ALL_TAGS, label: COPY.allTags },
            ...tags.map((t) => ({ value: t, label: t })),
          ]}
          onValueChange={(value) => setTag(value === null || value === ALL_TAGS ? null : value)}
          className="w-[160px]"
          testId={`${prefix}-tag`}
        />
        {canEdit && (
          <>
            <span
              className="ml-auto font-sans text-s12 text-text-tertiary max-sm:w-full"
              {...testIdProp(`${prefix}-add-helper`)}
            >
              {COPY.addHelper}
            </span>
            <Button
              appearance="saas"
              tone="primary"
              leadingIcon={<Icon icon={Upload} size={16} aria-hidden />}
              onClick={() => fileInput.current?.click()}
              testId={`${prefix}-add`}
            >
              {COPY.add}
            </Button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={ANY_ACCEPT}
              className="hidden"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = '';
                if (files.length > 0) void addFiles(files);
              }}
              {...testIdProp(`${prefix}-add-input`)}
            />
          </>
        )}
      </div>

      {failures.length > 0 && (
        <div
          className="flex items-start gap-8 rounded-lg border border-danger-200 bg-danger-50 p-12"
          {...testIdProp(`${prefix}-upload-errors`)}
        >
          <ul className="flex flex-1 flex-col gap-4">
            {failures.map((failure, i) => (
              <li key={i} className="font-sans text-s13 text-danger-700">
                {failure}
              </li>
            ))}
          </ul>
          <IconButton
            aria-label={COPY.dismiss}
            tone="secondary"
            size="sm"
            onClick={() => setFailures([])}
          >
            <Icon icon={X} size={14} aria-hidden />
          </IconButton>
        </div>
      )}

      {list.isPending ? (
        <Grid>
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="flex flex-col gap-8" {...testIdProp(`${prefix}-loading`)}>
              <Skeleton className="aspect-square w-full rounded-lg" />
              <Skeleton className="h-12 w-2/3" />
            </div>
          ))}
        </Grid>
      ) : list.isError ? (
        <Card testId={`${prefix}-error`}>
          <CardBody className="flex flex-col items-start gap-12">
            <p className="font-sans text-s14 text-text-secondary">{COPY.loadError}</p>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void list.refetch()}
              testId={`${prefix}-retry`}
            >
              {PROFILE_COPY.common.retry}
            </Button>
          </CardBody>
        </Card>
      ) : items.length === 0 && uploading.length === 0 ? (
        filtering ? (
          <p
            className="py-24 font-sans text-s14 text-text-secondary"
            {...testIdProp(`${prefix}-no-match`)}
          >
            {COPY.noMatch}
          </p>
        ) : (
          <EmptyState
            testId={`${prefix}-empty`}
            icon={<Icon icon={Images} size={20} aria-hidden />}
            title={COPY.empty.title}
            description={COPY.empty.body}
            action={
              canEdit ? (
                <Button
                  appearance="saas"
                  tone="primary"
                  leadingIcon={<Icon icon={Upload} size={16} aria-hidden />}
                  onClick={() => fileInput.current?.click()}
                >
                  {COPY.add}
                </Button>
              ) : undefined
            }
          />
        )
      ) : (
        <>
          <Grid>
            {uploading.map((upload) => (
              <div
                key={upload.id}
                className="flex min-w-0 flex-col gap-8"
                {...testIdProp(`${prefix}-uploading`)}
              >
                <Skeleton className="aspect-square w-full rounded-lg" />
                <span className="truncate font-sans text-s13 font-w600 text-text-strong">
                  {upload.name}
                </span>
                <span className="font-sans text-s12 text-text-secondary">{COPY.uploading}</span>
              </div>
            ))}
            {items.map((item) => (
              <CollateralTile
                key={item.id}
                orgRef={orgRef}
                item={item}
                canEdit={canEdit}
                onChanged={refresh}
                prefix={prefix}
              />
            ))}
          </Grid>
          {list.hasNextPage && (
            <div className="flex items-center justify-center gap-12">
              <span className="font-sans text-s13 text-text-tertiary">
                {COPY.count(items.length, first?.total ?? items.length)}
              </span>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
                testId={`${prefix}-more`}
              >
                {COPY.more}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Squares that fill the column: six across in the Profile page's wide column (mockup 12c). */
function Grid({ children }: { children: ReactNode }) {
  return (
    <div className="grid grid-cols-2 gap-x-12 gap-y-16 sm:grid-cols-[repeat(auto-fill,minmax(160px,1fr))]">
      {children}
    </div>
  );
}

function CollateralTile({
  orgRef,
  item,
  canEdit,
  onChanged,
  prefix,
}: {
  orgRef: string;
  item: CollateralItem;
  canEdit: boolean;
  onChanged: () => Promise<unknown>;
  prefix: string;
}) {
  const tile = `${prefix}-item-${item.id}`;
  const [dialog, setDialog] = useState<'edit' | 'delete' | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const replaceInput = useRef<HTMLInputElement>(null);

  const replace = useMutation({
    mutationFn: (file: File) => replaceOrgCollateralFile(orgRef, item.id, file),
    onMutate: () => setFailure(null),
    onSuccess: () => onChanged(),
    onError: (error) => setFailure(brandRefusalMessage(error, COPY.replaceFailed)),
  });
  const mark = useMutation({
    mutationFn: (outOfDate: boolean) => markOrgCollateralItem(orgRef, item.id, outOfDate),
    onMutate: () => setFailure(null),
    onSuccess: () => onChanged(),
    onError: (error) => setFailure(brandRefusalMessage(error, COPY.markFailed)),
  });

  // The address never changes on a Replace, so the tile asks for the new file by its replace time.
  const src = versionedUrl(item.url, item.replacedAt);
  const subline = collateralSubline(item);
  const busy = replace.isPending || mark.isPending;

  return (
    <div className="group flex min-w-0 flex-col gap-8" {...testIdProp(tile)}>
      <div className="relative">
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          aria-label={COPY.open(item.title)}
          className="block rounded-lg focus-visible:shadow-ring focus-visible:outline-none"
        >
          {item.kind === 'image' ? (
            <img
              src={src}
              alt=""
              loading="lazy"
              className={cn(
                'aspect-square w-full rounded-lg bg-surface-sunken object-cover ring-1 ring-border-subtle',
                item.outOfDate && 'opacity-45 grayscale',
                busy && 'opacity-60',
              )}
            />
          ) : (
            <span
              className={cn(
                'flex aspect-square w-full flex-col items-center justify-center gap-8 rounded-lg bg-surface-sunken ring-1 ring-border-subtle',
                item.outOfDate && 'opacity-45',
              )}
            >
              <Icon icon={FileText} size={32} className="text-text-tertiary" aria-hidden />
              <span className="font-sans text-s12 font-w600 text-text-tertiary">{COPY.pdf}</span>
            </span>
          )}
        </a>
        {canEdit && (
          <span className="absolute top-6 right-6">
            <Menu.Root modal={false}>
              <Menu.Trigger
                disabled={busy}
                aria-label={COPY.actions(item.title)}
                render={
                  <IconButton
                    aria-label={COPY.actions(item.title)}
                    tone="secondary"
                    size="sm"
                    className="bg-white shadow-sm"
                    testId={`${tile}-menu`}
                  />
                }
              >
                <Icon icon={MoreHorizontal} size={16} aria-hidden />
              </Menu.Trigger>
              <Menu.Content saas align="end" sideOffset={6}>
                <Menu.Item
                  saas
                  startIcon={<Icon icon={RefreshCw} size={15} aria-hidden />}
                  onClick={() => replaceInput.current?.click()}
                  testId={`${tile}-replace`}
                >
                  {COPY.menu.replace}
                </Menu.Item>
                <Menu.Item
                  saas
                  startIcon={<Icon icon={Pencil} size={15} aria-hidden />}
                  onClick={() => setDialog('edit')}
                  testId={`${tile}-edit`}
                >
                  {COPY.menu.edit}
                </Menu.Item>
                <Menu.Item
                  saas
                  startIcon={
                    <Icon icon={item.outOfDate ? ArchiveRestore : Archive} size={15} aria-hidden />
                  }
                  onClick={() => mark.mutate(!item.outOfDate)}
                  testId={`${tile}-mark`}
                >
                  {item.outOfDate ? COPY.menu.markCurrent : COPY.menu.markOutOfDate}
                </Menu.Item>
                <Menu.Separator />
                <Menu.Item
                  saas
                  tone="danger"
                  startIcon={<Icon icon={Trash2} size={15} aria-hidden />}
                  onClick={() => setDialog('delete')}
                  testId={`${tile}-delete`}
                >
                  {COPY.menu.delete}
                </Menu.Item>
              </Menu.Content>
            </Menu.Root>
            <input
              ref={replaceInput}
              type="file"
              accept={item.kind === 'image' ? IMAGE_ACCEPT : 'application/pdf'}
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                const refusal = brandUploadRefusal(
                  file,
                  item.kind === 'image' ? 'collateral-image' : 'collateral-file',
                );
                if (refusal !== null) setFailure(refusal);
                else replace.mutate(file);
              }}
              {...testIdProp(`${tile}-replace-input`)}
            />
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <span className="truncate font-sans text-s13 font-w600 text-text-strong">{item.title}</span>
        {subline !== null && (
          <span className="truncate font-sans text-s12 text-text-secondary">{subline}</span>
        )}
      </div>
      {item.outOfDate ? (
        <span className="flex flex-col items-start gap-2">
          <span className="font-sans text-s12 text-text-tertiary">
            <span className="font-w600" {...testIdProp(`${tile}-out-of-date`)}>
              {COPY.outOfDate}
            </span>
            {'. '}
            {COPY.outOfDateNote}
          </span>
          {canEdit && (
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              disabled={busy}
              onClick={() => mark.mutate(false)}
              testId={`${tile}-put-back`}
            >
              {COPY.putBack}
            </Button>
          )}
        </span>
      ) : item.tags.length > 0 ? (
        <span className="flex flex-wrap gap-4">
          {item.tags.map((t) => (
            <Badge key={t} tone="neutral" size="md">
              {t}
            </Badge>
          ))}
        </span>
      ) : null}
      {failure !== null && <ErrorMessage testId={`${tile}-error`}>{failure}</ErrorMessage>}
      {dialog === 'edit' && (
        <EditDetailsDialog
          orgRef={orgRef}
          item={item}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await onChanged();
          }}
          prefix={tile}
        />
      )}
      {dialog === 'delete' && (
        <DeleteDialog
          orgRef={orgRef}
          item={item}
          onClose={() => setDialog(null)}
          onDeleted={async () => {
            setDialog(null);
            await onChanged();
          }}
          prefix={tile}
        />
      )}
    </div>
  );
}

/** The server's bounds on an item's tags (`MAX_TAGS`, `MAX_TAG`), said before Save: null when they hold. */
export function collateralTagProblem(tags: readonly string[]): string | null {
  if (tags.some((tag) => tag.length > 40)) return COPY.edit.tagTooLong;
  if (new Set(tags.map((tag) => tag.toLocaleLowerCase())).size > 20) return COPY.edit.tooManyTags;
  return null;
}

/** Only what changed, so an untouched field is never repeated (the PATCH leaves an absent one as is). */
export function collateralPatch(
  item: CollateralItem,
  form: { title: string; description: string; tags: string; datedOn: string },
): CollateralItemPatch {
  const patch: CollateralItemPatch = {};
  const title = form.title.trim().replace(/\s+/g, ' ');
  if (title !== item.title) patch.title = title;
  const description = form.description.trim();
  if ((description.length > 0 ? description : null) !== item.description) {
    patch.description = description.length > 0 ? description : null;
  }
  const tags = tagsFromText(form.tags);
  if (JSON.stringify(tags) !== JSON.stringify(item.tags)) patch.tags = tags;
  const datedOn = form.datedOn.length > 0 ? form.datedOn : null;
  if (datedOn !== item.datedOn) patch.dated_on = datedOn;
  return patch;
}

function EditDetailsDialog({
  orgRef,
  item,
  onClose,
  onSaved,
  prefix,
}: {
  orgRef: string;
  item: CollateralItem;
  onClose: () => void;
  onSaved: () => Promise<void>;
  prefix: string;
}) {
  const [form, setForm] = useState({
    title: item.title,
    description: item.description ?? '',
    tags: item.tags.join(', '),
    datedOn: item.datedOn ?? '',
  });
  const [tried, setTried] = useState(false);
  const titleMissing = form.title.trim().length === 0;
  const tagProblem = collateralTagProblem(tagsFromText(form.tags));
  const save = useMutation({
    mutationFn: (patch: CollateralItemPatch) => updateOrgCollateralItem(orgRef, item.id, patch),
    onSuccess: () => onSaved(),
  });
  const submit = () => {
    setTried(true);
    if (titleMissing || tagProblem !== null) return;
    const patch = collateralPatch(item, form);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    save.mutate(patch);
  };
  return (
    <Dialog.Root open onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <Dialog.Content
        aria-label={COPY.edit.title}
        width={480}
        className="flex flex-col gap-16"
        testId={`${prefix}-edit-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {COPY.edit.title}
        </Dialog.Title>
        <Field
          label={COPY.edit.name}
          value={form.title}
          maxLength={120}
          onChange={(event) => setForm({ ...form, title: event.target.value })}
          error={tried && titleMissing}
          errorMessage={tried && titleMissing ? COPY.edit.titleRequired : undefined}
          testId={`${prefix}-edit-title`}
        />
        <Textarea
          label={COPY.edit.description}
          value={form.description}
          maxLength={500}
          rows={2}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
          testId={`${prefix}-edit-description`}
        />
        <Field
          label={COPY.edit.tags}
          helper={COPY.edit.tagsHelper}
          value={form.tags}
          onChange={(event) => setForm({ ...form, tags: event.target.value })}
          error={tried && tagProblem !== null}
          errorMessage={tried && tagProblem !== null ? tagProblem : undefined}
          testId={`${prefix}-edit-tags`}
        />
        <Field
          label={COPY.edit.dated}
          helper={COPY.edit.datedHelper}
          type="date"
          value={form.datedOn}
          min="1900-01-01"
          max="2100-12-31"
          className="w-[200px]"
          onChange={(event) => setForm({ ...form, datedOn: event.target.value })}
          testId={`${prefix}-edit-dated`}
        />
        {save.isError && (
          <ErrorMessage testId={`${prefix}-edit-error`}>
            {brandRefusalMessage(save.error, COPY.edit.failed, COPY.edit.fieldRefused)}
          </ErrorMessage>
        )}
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={save.isPending}
            render={<Button appearance="saas" tone="secondary" size="md" />}
          >
            {COPY.edit.cancel}
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="primary"
            size="md"
            loading={save.isPending}
            onClick={submit}
            testId={`${prefix}-edit-save`}
          >
            {COPY.edit.save}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function DeleteDialog({
  orgRef,
  item,
  onClose,
  onDeleted,
  prefix,
}: {
  orgRef: string;
  item: CollateralItem;
  onClose: () => void;
  onDeleted: () => Promise<void>;
  prefix: string;
}) {
  const remove = useMutation({
    mutationFn: () => deleteOrgCollateralItem(orgRef, item.id),
    onSuccess: () => onDeleted(),
  });
  return (
    <Dialog.Root open onOpenChange={(open) => !open && !remove.isPending && onClose()}>
      <Dialog.Content
        aria-label={COPY.delete.title}
        className="flex flex-col gap-16"
        testId={`${prefix}-delete-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {COPY.delete.title}
        </Dialog.Title>
        <p className="font-sans text-s15 font-w600 tracking-givmo break-words text-text-strong">
          {item.title}
        </p>
        <p className="font-sans text-s13 leading-h15 tracking-givmo text-text-secondary">
          {COPY.delete.body}
        </p>
        {remove.isError && (
          <ErrorMessage testId={`${prefix}-delete-error`}>
            {brandRefusalMessage(remove.error, COPY.delete.failed)}
          </ErrorMessage>
        )}
        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={remove.isPending}
            render={<Button appearance="saas" tone="secondary" size="md" />}
          >
            {COPY.delete.cancel}
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="danger"
            size="md"
            loading={remove.isPending}
            onClick={() => remove.mutate()}
            testId={`${prefix}-delete-confirm`}
          >
            {COPY.delete.confirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
